import { useEffect, useRef, useState } from 'react';
import { EventBus, useGameStore } from '@campus/core';

/**
 * ChatBubbles — Bulles de chat proximity au-dessus des avatars.
 *
 * PATTERN : RAF + mutation DOM directe + callback refs O(1)
 *   (identique à PlayerNameplates — cf. commentaire de ce fichier)
 *
 * TTL : chaque bulle disparaît 5s après la réception du message.
 * Déduplique par senderId : seul le message le plus récent est affiché.
 *
 * Les positions arrivent de Phaser via EventBus 'overlay:positions-updated'.
 * Le RAF applique les positions directement sur le DOM sans passer par React.
 */

const BUBBLE_TTL_MS = 5_000;

export function ChatBubbles() {
  const positionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  // Map<senderId, HTMLElement> — mis à jour par callback refs
  const domNodesRef = useRef<Map<string, HTMLElement>>(new Map());

  const proximityMessages = useGameStore((s) => s.proximityMessages);

  // Force re-render quand les TTL expirent pour retirer les bulles périmées
  const [, forceUpdate] = useState(0);

  // RAF loop : applique les positions via transform direct
  useEffect(() => {
    const handlePositions = (positions: Map<string, { x: number; y: number }>) => {
      positionsRef.current = positions;
    };

    EventBus.on('overlay:positions-updated', handlePositions);

    let rafId: number;
    const updateDOM = () => {
      // Itère sur les DOM nodes (bulles actives) et cherche leur position.
      // Sens inverse de l'ancienne boucle : on part des bulles, pas des positions,
      // pour ne pas rater un senderId absent de positionsRef à un instant T.
      domNodesRef.current.forEach((el, senderId) => {
        const pos = positionsRef.current.get(senderId);
        if (pos) {
          // pos.y = bord supérieur du sprite en pixels écran.
          // Empilement (bas → haut) : sprite → nameplate (~22px) → gap 4px → bulle
          // -100% = hauteur propre de la bulle (translate relatif à l'élément).
          el.style.transform = `translate3d(calc(${pos.x}px - 50%), calc(${pos.y}px - 100% - 28px), 0)`;
        }
        // DEBUG — retirer une fois la position confirmée
        else {
          console.log(
            `[ChatBubbles] RAF — pas de position pour senderId=${senderId}`,
            `positions dispo: [${[...positionsRef.current.keys()].join(', ')}]`,
          );
        }
      });
      rafId = requestAnimationFrame(updateDOM);
    };

    rafId = requestAnimationFrame(updateDOM);

    return () => {
      cancelAnimationFrame(rafId);
      EventBus.off('overlay:positions-updated', handlePositions);
    };
  }, []);

  // Planifie un re-render à l'expiration de chaque message encore actif
  useEffect(() => {
    const now = Date.now();
    const timers = proximityMessages
      .filter((msg) => now - msg.timestamp < BUBBLE_TTL_MS)
      .map((msg) => {
        const remaining = BUBBLE_TTL_MS - (now - msg.timestamp);
        return setTimeout(() => forceUpdate((n) => n + 1), remaining);
      });

    return () => timers.forEach(clearTimeout);
  }, [proximityMessages]);

  // Déduplique par clé de position, garde le message le plus récent, filtre les expirés.
  // Clé = "self:{senderId}" pour les messages isSelf (joueur local),
  //       "{senderId}"       pour les messages reçus (joueurs distants).
  // Cela évite la collision quand deux joueurs partagent le même userId (dev).
  const now = Date.now();
  const activeBubbles = new Map<string, typeof proximityMessages[0]>();
  for (const msg of proximityMessages) {
    if (now - msg.timestamp < BUBBLE_TTL_MS) {
      const key = msg.isSelf ? `self:${msg.senderId}` : msg.senderId;
      activeBubbles.set(key, msg);
    }
  }

  return (
    <div
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      aria-hidden="true"
    >
      {[...activeBubbles.entries()].map(([posKey, msg]) => (
        <div
          key={posKey}
          ref={(node) => {
            if (node) domNodesRef.current.set(posKey, node);
            else domNodesRef.current.delete(posKey);
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            willChange: 'transform',
            // Position initiale neutre — le RAF applique translate3d avec offset centrage
            transform: 'translate3d(-9999px, -9999px, 0)',
          }}
        >
          <ChatBubble text={msg.text} {...(msg.isSelf ? { isSelf: true } : {})} />
        </div>
      ))}
    </div>
  );
}

// ── Sous-composant de rendu ───────────────────────────────────

function ChatBubble({ text, isSelf }: { text: string; isSelf?: boolean }) {
  return (
    <div
      style={{
        background: isSelf ? 'rgba(0, 100, 220, 0.85)' : 'rgba(30, 30, 30, 0.80)',
        color: 'white',
        padding: '5px 10px',
        borderRadius: '12px',
        fontSize: '13px',
        fontFamily: 'var(--font-body)',
        maxWidth: '180px',
        wordBreak: 'break-word',
        userSelect: 'none',
        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        lineHeight: 1.3,
      }}
    >
      {text}
    </div>
  );
}
